import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { Resources } from "Translations/Resources";

export type ChatChannelCategory =
  | "All"
  | "Mutual"
  | "Shopping"
  | "Groups"
  | "Requests";

const SOCIAL_PERSONA_CATEGORIES: ReadonlyArray<ChatChannelCategory> = [
  "All",
  "Mutual",
  "Shopping",
  "Groups",
  "Requests",
];

const HUNTER_CATEGORIES: ReadonlyArray<ChatChannelCategory> = [
  "All",
  "Shopping",
  "Groups",
  "Requests",
];

/**
 * Which tabs a persona gets. Only Customers follow, so only they can have mutual followers
 * (Rules v2 C2, `canRoleFollow`) — Mutual is a social-persona tab only.
 * Shopping — DMs whose other member is a merchant — is meaningless to a merchant but is a hunter's
 * "my merchants" view, labelled accordingly (getChatCategoryLabelResource). Roles not listed
 * (customer, back-office) get the full set, as before.
 */
const CATEGORIES_BY_BUSINESS_ROLE: Partial<
  Record<string, ReadonlyArray<ChatChannelCategory>>
> = {
  [AccessRoleCodes.Merchant]: ["All", "Groups", "Requests"],
  [AccessRoleCodes.Hunter]: HUNTER_CATEGORIES,
  [AccessRoleCodes.HunterPlus]: HUNTER_CATEGORIES,
};

export const getChatChannelCategoriesForRole = (
  roleCode: string | null | undefined,
): ReadonlyArray<ChatChannelCategory> =>
  (roleCode ? CATEGORIES_BY_BUSINESS_ROLE[roleCode] : undefined) ??
  SOCIAL_PERSONA_CATEGORIES;

const CATEGORY_RESOURCES = Resources.Chat.Categories;

const LABEL_BY_CATEGORY: Record<ChatChannelCategory, string> = {
  All: CATEGORY_RESOURCES.All,
  Mutual: CATEGORY_RESOURCES.Mutual,
  Shopping: CATEGORY_RESOURCES.Shopping,
  Groups: CATEGORY_RESOURCES.Groups,
  Requests: CATEGORY_RESOURCES.Requests,
};

const isHunterRole = (roleCode: string | null | undefined): boolean =>
  roleCode === AccessRoleCodes.Hunter ||
  roleCode === AccessRoleCodes.HunterPlus;

/** Resource path of a tab's label; the Shopping filter reads "Merchants" for a hunter. */
export const getChatCategoryLabelResource = (
  category: ChatChannelCategory,
  roleCode: string | null | undefined,
): string =>
  category === "Shopping" && isHunterRole(roleCode)
    ? CATEGORY_RESOURCES.Merchants
    : LABEL_BY_CATEGORY[category];
